import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { useToast } from '../components/Toast';
import { useAuth, type PendingAdd } from './auth';
import { savedRepo, type SavedArtist } from './savedRepo';

const key = (userId: string | undefined) => ['saved', userId] as const;

export function useSavedArtists() {
  const { user } = useAuth();
  return useQuery({
    queryKey: key(user?.id),
    queryFn: () => savedRepo.list(user!.id),
    enabled: Boolean(user),
    staleTime: 30_000,
  });
}

export function useIsSaved(artistId: string | undefined): boolean {
  const { data } = useSavedArtists();
  return Boolean(artistId && data?.some((a) => a.id === artistId));
}

type Vars = { op: 'add'; artist: SavedArtist } | { op: 'remove'; artist: SavedArtist } | { op: 'addMany'; artists: SavedArtist[] };

/** Add / remove with optimistic updates; the list changes instantly and rolls back on failure. */
export function useSavedArtistMutations() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const k = key(user?.id);

  const mutation = useMutation({
    mutationFn: async (v: Vars) => {
      if (!user) throw new Error('Not signed in');
      if (v.op === 'addMany') await savedRepo.addMany(user.id, v.artists);
      else if (v.op === 'add') await savedRepo.add(user.id, v.artist);
      else await savedRepo.remove(user.id, v.artist.id);
    },
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: k });
      const prev = qc.getQueryData<SavedArtist[]>(k);
      const incoming = v.op === 'remove' ? [] : v.op === 'add' ? [v.artist] : v.artists;
      const removedId = v.op === 'remove' ? v.artist.id : null;
      qc.setQueryData<SavedArtist[]>(k, (cur = []) =>
        removedId ? cur.filter((a) => a.id !== removedId) : [...cur, ...incoming.filter((a) => !cur.some((x) => x.id === a.id))],
      );
      return { prev };
    },
    onError: (_err, v, ctx) => {
      qc.setQueryData(k, ctx?.prev);
      toast({
        message:
          v.op === 'addMany'
            ? `Couldn’t add those ${v.artists.length} artists. Try again.`
            : v.op === 'add'
              ? `Couldn’t add ${v.artist.name}. Try again.`
              : `Couldn’t remove ${v.artist.name}. Try again.`,
      });
    },
    onSettled: () => qc.invalidateQueries({ queryKey: k }),
  });

  const { mutate } = mutation;

  const add = useCallback(
    (a: Pick<SavedArtist, 'id' | 'name' | 'imageUrl'> & { createdAt?: string }) =>
      mutate({ op: 'add', artist: { ...a, createdAt: a.createdAt ?? new Date().toISOString() } }),
    [mutate],
  );

  const addMany = useCallback(
    (list: Pick<SavedArtist, 'id' | 'name' | 'imageUrl'>[], { onSuccess }: { onSuccess?: () => void } = {}) => {
      const createdAt = new Date().toISOString();
      mutate({ op: 'addMany', artists: list.map((a) => ({ ...a, createdAt })) }, { onSuccess });
    },
    [mutate],
  );

  const remove = useCallback(
    (a: SavedArtist, { undo = false } = {}) => {
      mutate({ op: 'remove', artist: a });
      if (undo) {
        toast({
          message: `Removed ${a.name}`,
          action: { label: 'Undo', onClick: () => mutate({ op: 'add', artist: a }) },
        });
      }
    },
    [mutate, toast],
  );

  return { add, addMany, remove, isPending: mutation.isPending };
}

/**
 * The one entry point UI uses for "Add to My Artists": signed in → toggles; signed out →
 * opens sign-in and remembers the artist so it's saved after the redirect.
 */
export function useToggleSaved() {
  const { user, openSignIn } = useAuth();
  const { data } = useSavedArtists();
  const { add, remove } = useSavedArtistMutations();
  return useCallback(
    (artist: PendingAdd) => {
      if (!user) {
        openSignIn({ pending: artist });
        return;
      }
      const existing = data?.find((a) => a.id === artist.id);
      if (existing) remove(existing);
      else add(artist);
    },
    [user, data, add, remove, openSignIn],
  );
}

/** Completes a pending add once the user is back from sign-in (FR-U4). */
export function usePendingAddCompletion() {
  const { user, takePendingAdd } = useAuth();
  const { add } = useSavedArtistMutations();
  const toast = useToast();
  const done = useRef<string | null>(null);
  useEffect(() => {
    if (!user || done.current === user.id) return;
    done.current = user.id;
    const pending = takePendingAdd();
    if (pending) {
      add(pending);
      toast({ message: `Added ${pending.name} to My Artists` });
    }
  }, [user, takePendingAdd, add, toast]);
}

/** Keeps the cached image fresh when an artist page loads (image_url is a cache, see data model). */
export function useRefreshSavedImage(artistId: string | undefined, imageUrl: string | null | undefined) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data } = useSavedArtists();
  useEffect(() => {
    if (!user || !artistId || imageUrl === undefined) return;
    const saved = data?.find((a) => a.id === artistId);
    if (!saved || saved.imageUrl === imageUrl) return;
    savedRepo
      .updateImage(user.id, artistId, imageUrl)
      .then(() => qc.invalidateQueries({ queryKey: key(user.id) }))
      .catch(() => {
        /* cosmetic; ignore */
      });
  }, [user, artistId, imageUrl, data, qc]);
}
